import { existsSync, readdirSync, readFileSync, statSync } from "node:fs";
import { basename, join, relative } from "node:path";

import { CLAUDE_PROJECTS_DIR, CODEX_SESSIONS_DIR } from "../constants.js";
import { isRecord } from "./fs.js";
import type { FounderLoopImport } from "./self-audit.js";

export const AI_SESSION_SOURCES = ["codex", "claude"] as const;

export type AiSessionSource = (typeof AI_SESSION_SOURCES)[number];

export interface LoadAiSessionImportsOptions {
  claudeProjectsDir?: string;
  codexSessionsDir?: string;
  limitPerSource?: number;
  maxBytesPerFile?: number;
  now?: Date;
  sinceDays?: number;
  sources: readonly AiSessionSource[];
}

export interface LoadAiSessionImportsResult {
  connectedSources: string[];
  imports: FounderLoopImport[];
  missingSources: string[];
  retainedLines: number;
  scannedFiles: number;
  scannedMessages: number;
  skippedFiles: number;
}

interface CandidateFile {
  mtimeMs: number;
  path: string;
  source: AiSessionSource;
}

interface ReadSessionImportResult {
  import: FounderLoopImport;
  messageCount: number;
  retainedLineCount: number;
}

const DEFAULT_LIMIT_PER_SOURCE = 3;
const DEFAULT_SINCE_DAYS = 30;
const DEFAULT_MAX_BYTES_PER_FILE = 1_000_000;
const AUDIT_RELEVANT_LINE_PATTERN =
  /\b(decision|decided|artifact|receipt|proof|commitment|committed|next action|follow[- ]?up|outcome|result|impact|roi|economics|token|cost|saved|open loop|gap|blocker|risk|owner|dri|writeback|rollback|quality score|mcp|tool call|orgx_emit_activity|orgx_apply_changeset|complete_with_proof|orgx_write|mcp__orgx__)\b/i;
const STRONG_AUDIT_LINE_PATTERN =
  /^(?:[-*]\s*)?(?:decision|artifact|receipt|proof|commitment|next action|follow[- ]?up|outcome|result|impact|roi|economics|open loop|gap|blocker|risk|owner|dri|rollback|quality score)\s*:/i;
const ORGX_TOOL_SIGNAL_PATTERN =
  /\b(?:mcp__orgx__|orgx_emit_activity|orgx_apply_changeset|complete_with_proof|orgx_write|orgx_inspect|ship_batch|record_outcome|orgx_free_audit)\b/i;
const INSTRUCTION_BOILERPLATE_PATTERN =
  /^(?:#{1,6}\s*)?(?:agents\.md|instructions?|codex guardrails|precedence|read before you write|use exactly what was specified|don'?t confuse technologies|repo \+ scope hygiene|initiative \/ mcp discipline|ui quality bar|verification standards|secrets \+ safety|patch\/editing rules|commit \/ pr \/ merge protocol|output style|mcp integration|agent domains|skills|conventions)\b/i;
const IMPERATIVE_BOILERPLATE_PATTERN =
  /^[-*]\s+(?:if the user|when the user|if mcp|when agents|never|always|do not|don'?t|default to|treat|produce|pick|verify|avoid|match|use exactly|keep system boundaries|quote bracketed|for orgx|when capture tooling|if no meaningful|do \*\*not\*\*)\b/i;
const CODE_OR_DOC_NOISE_PATTERN =
  /^(?:[-*]\s*)?(?:https?:\/\/|\/|\.{0,2}\/|[A-Za-z]:\/|`{1,3}|"{1,2}[A-Za-z0-9:_-]+":|\d+\s*$|\d+\t|import\s|export\s|const\s|let\s|var\s|function\s|type\s|interface\s|CREATE\s+TABLE|ALTER\s+TABLE|SELECT\s+|INSERT\s+|UPDATE\s+|DELETE\s+)/i;
const EMPTY_AUDIT_LABEL_PATTERN =
  /^(?:[-*]\s*)?(?:decision|artifact|receipt|proof|commitment|next action|follow[- ]?up|outcome|result|impact|roi|economics|open loop|gap|blocker|risk|owner|dri|rollback|quality score)\s*:\s*[\[{(;,]*$/i;
const LABEL_VALUE_NOISE_PATTERN =
  /^(?:[-*]\s*)?(?:decision|artifact|receipt|proof|commitment|next action|follow[- ]?up|outcome|result|impact|roi|economics|open loop|gap|blocker|risk|owner|dri|rollback|quality score)\s*:\s*(?:["']?[\w-]+["']?\s*\|\s*["']?[\w-]+["']?|[\d\s.,%$]+)[,;)]*$/i;
const TYPE_SIGNATURE_NOISE_PATTERN =
  /^(?:[-*]\s*)?(?:decision|artifact|receipt|proof|commitment|next action|follow[- ]?up|outcome|result|impact|roi|economics|open loop|gap|blocker|risk|owner|dri|rollback|quality score)\s*:\s*[A-Za-z_$][\w$]*(?:<[^>]+>)?[\s,;)]*$/i;
const TOOL_CATALOG_NOISE_PATTERN =
  /^(?:[-*]\s*)?(?:[a-z][\w.:-]*\s*\([^)]+\),?\s*){2,}$/i;
const BARE_TOOL_SIGNAL_PATTERN =
  /^(?:\d+\.\s*|[-*]\s*)?(?:mcp__orgx__[\w-]*|orgx_emit_activity|orgx_apply_changeset|complete_with_proof|orgx_write|orgx_inspect|ship_batch|record_outcome|orgx_free_audit)\s*$/i;
const TOOL_QUESTION_NOISE_PATTERN =
  /^(?:\d+\.\s*)?\*\*[^*]+\*\*.*\?\s*(?:\([^)]*\b(?:mcp|orgx_write|start_plan_session)\b[^)]*\)\.?)?$/i;
const SOURCE_REFERENCE_NOISE_PATTERN =
  /(?:^|\s)(?:\.{0,2}\/)?(?:orgx|src|lib|workers|public|scripts|tests|apps|packages)\/[\w./-]+\.(?:ts|tsx|js|jsx|json|md|txt|sql|py)(?::\d+)?\b/i;
const FILE_REFERENCE_NOISE_PATTERN =
  /\b[\w.-]+\.(?:ts|tsx|js|jsx|json|md|txt|sql|py)(?::\d+(?:[–-]\d+)?)?\b/i;
const LOCAL_ASSET_REFERENCE_PATTERN =
  /(?:<BlogImage\b|source:\s*\/Users\/|\/blog\/[\w./-]+\.(?:png|jpg|jpeg|webp|gif))/i;
const LINE_NUMBER_SOURCE_NOISE_PATTERN =
  /^\d+(?::|\s+)\s*/i;
const TOOL_WORKFLOW_NOISE_PATTERN =
  /(?:→|->).*\b(?:orgx_write|orgx_spawn|record_outcome|submit_learning|orgx_recommend|mcp__orgx__)\b/i;
const TOOL_FAILURE_PATTERN =
  /^(?:\d+\.\s*)?(?:mcp__orgx__[\w-]*|orgx_emit_activity|orgx_apply_changeset|complete_with_proof|orgx_write|orgx_inspect|ship_batch|record_outcome|orgx_free_audit)\b.*\b(?:rejected|requires|failed|fails|error|invalid|unavailable|timeout|timed out|not\s+(?:called|dispatched|wired|created|available)|missing|broken)\b/i;
const RUNTIME_FAILURE_PATTERN =
  /\b(?:mcp__orgx__[\w-]*|orgx_emit_activity|orgx_apply_changeset|complete_with_proof|orgx_write|orgx_inspect|ship_batch|record_outcome|submit_learning|orgx_spawn|agent run)\b.*\b(?:rejected|requires|failed|fails|error|invalid|unavailable|timeout|timed out|never|no\s+\w+|not\s+(?:called|dispatched|wired|created|available|append)|missing|broken|only\s+in\s+agent\s+skill\s+prompts)\b/i;
const PROCESS_NARRATION_NOISE_PATTERN =
  /^(?:now i\b|let me\b|i (?:have|need|will|can)\b|i['’]ll\b|i['’]m\b|so\b.*\b(?:confirm|check|verify|fetches)\b)/i;

export function parseAiSessionSources(value?: string): AiSessionSource[] {
  if (!value?.trim()) return [];

  const requested = value
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const expanded = requested.includes("all") ? [...AI_SESSION_SOURCES] : requested;
  const deduped = [...new Set(expanded)];
  const invalid = deduped.filter((source) => !AI_SESSION_SOURCES.includes(source as AiSessionSource));
  if (invalid.length > 0) {
    throw new Error(`Unsupported AI-session source: ${invalid.join(", ")}. Use codex, claude, or all.`);
  }

  return deduped as AiSessionSource[];
}

export function filterSupportedAiSessionSources(sources: readonly string[]): AiSessionSource[] {
  const filtered = sources
    .map((source) => source.trim().toLowerCase().replace(/-/g, "_"))
    .map((source) => source === "claude_code" ? "claude" : source)
    .filter((source): source is AiSessionSource =>
      AI_SESSION_SOURCES.includes(source as AiSessionSource)
    );
  return [...new Set(filtered)];
}

function parseJsonLine(line: string): unknown {
  try {
    return JSON.parse(line);
  } catch {
    return null;
  }
}

function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (!isRecord(item)) return "";
        if (typeof item.text === "string") return item.text;
        if (typeof item.content === "string") return item.content;
        return "";
      })
      .filter(Boolean)
      .join("\n");
  }
  if (isRecord(value) && typeof value.text === "string") return value.text;
  return "";
}

function stringFromRecord(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function numberFromRecord(record: Record<string, unknown>, key: string): number | undefined {
  const value = record[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function normalizeJsonAuditLine(line: string): string | null {
  if (!line.startsWith("{")) return null;

  const parsed = parseJsonLine(line);
  if (!isRecord(parsed)) return null;

  const summary = stringFromRecord(parsed, "summary");
  const stats = isRecord(parsed.summary_stats) ? parsed.summary_stats : undefined;
  const requestedCount = stats ? numberFromRecord(stats, "requested_count") : undefined;
  const createdCount = stats ? numberFromRecord(stats, "created_count") : undefined;
  const failedCount = stats ? numberFromRecord(stats, "failed_count") : undefined;

  const hierarchy = isRecord(parsed.hierarchy) ? parsed.hierarchy : undefined;
  const initiative = hierarchy && isRecord(hierarchy.initiative) ? hierarchy.initiative : undefined;
  const initiativeTitle = initiative ? stringFromRecord(initiative, "title") ?? stringFromRecord(initiative, "name") : undefined;
  const initiativeError = initiative ? stringFromRecord(initiative, "error") : undefined;

  if (
    summary &&
    requestedCount !== undefined &&
    createdCount !== undefined &&
    failedCount !== undefined &&
    /\bcreated\b/i.test(summary)
  ) {
    const titleSuffix = initiativeTitle ? ` for ${initiativeTitle}` : "";
    const errorSuffix = initiativeError ? `; ${initiativeError.replace(/\.+$/, "")}` : "";
    if (failedCount > 0) {
      return `Blocker: OrgX scaffold created ${createdCount}/${requestedCount} entities${titleSuffix}${errorSuffix}.`;
    }
    return `Artifact: OrgX scaffold created ${createdCount}/${requestedCount} entities${titleSuffix}.`;
  }

  return null;
}

function normalizeAuditRelevantLine(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const jsonLine = normalizeJsonAuditLine(trimmed);
  if (jsonLine) return jsonLine;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return null;

  if (SOURCE_REFERENCE_NOISE_PATTERN.test(trimmed)) return null;
  if (FILE_REFERENCE_NOISE_PATTERN.test(trimmed)) return null;
  if (LOCAL_ASSET_REFERENCE_PATTERN.test(trimmed)) return null;
  if (LINE_NUMBER_SOURCE_NOISE_PATTERN.test(trimmed)) return null;
  if (/^audit\s+\/Users\//i.test(trimmed)) return null;
  if (/^#{1,6}\s/.test(trimmed)) return null;
  if (PROCESS_NARRATION_NOISE_PATTERN.test(trimmed)) return null;
  if (/^(?:\d+\.\s*)?(?:[-*]\s*)?(?:\*\*)?\b(?:integration gaps|gaps between)\b/i.test(trimmed)) return null;
  if (/^\s*[-*]\s+the `?orgx`? mcp tools listed/i.test(trimmed)) return null;
  if (TOOL_WORKFLOW_NOISE_PATTERN.test(trimmed)) return null;

  const toolFailure = trimmed.match(TOOL_FAILURE_PATTERN);
  if (toolFailure) {
    return `Blocker: ${trimmed.replace(/^\d+\.\s*/, "")}`;
  }
  if (RUNTIME_FAILURE_PATTERN.test(trimmed)) {
    const label = /^no programmatic\b/i.test(trimmed) ? "Gap" : "Blocker";
    return `${label}: ${trimmed.replace(/^\d+\.\s*/, "")}`;
  }

  if (!AUDIT_RELEVANT_LINE_PATTERN.test(trimmed)) return null;
  if (!STRONG_AUDIT_LINE_PATTERN.test(trimmed) && !ORGX_TOOL_SIGNAL_PATTERN.test(trimmed)) return null;
  if (INSTRUCTION_BOILERPLATE_PATTERN.test(trimmed)) return null;
  if (IMPERATIVE_BOILERPLATE_PATTERN.test(trimmed)) return null;
  if (CODE_OR_DOC_NOISE_PATTERN.test(trimmed)) return null;
  if (EMPTY_AUDIT_LABEL_PATTERN.test(trimmed)) return null;
  if (LABEL_VALUE_NOISE_PATTERN.test(trimmed)) return null;
  if (TYPE_SIGNATURE_NOISE_PATTERN.test(trimmed)) return null;
  if (TOOL_CATALOG_NOISE_PATTERN.test(trimmed)) return null;
  if ((trimmed.match(/\b[a-z][\w.:-]*\s*\([^)]+\)/g) ?? []).length >= 2) return null;
  if (BARE_TOOL_SIGNAL_PATTERN.test(trimmed)) return null;
  if (TOOL_QUESTION_NOISE_PATTERN.test(trimmed)) return null;
  if (/^[-*]\s+all agents\b/i.test(trimmed)) return null;

  return trimmed;
}

function extractCodexMessageText(record: unknown): string {
  if (!isRecord(record) || record.type !== "response_item" || !isRecord(record.payload)) {
    return "";
  }

  const payload = record.payload;
  if (payload.type !== "message" || (payload.role !== "user" && payload.role !== "assistant")) {
    return "";
  }

  return asText(payload.content);
}

function extractClaudeMessageText(record: unknown): string {
  if (!isRecord(record) || record.isMeta === true || !isRecord(record.message)) {
    return "";
  }

  const message = record.message;
  if (message.role !== "user" && message.role !== "assistant") {
    return "";
  }

  const text = asText(message.content);
  if (/^<local-command-caveat>/i.test(text.trim())) return "";
  return text;
}

function keepAuditRelevantLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => normalizeAuditRelevantLine(line))
    .filter((line): line is string => Boolean(line));
}

function collectJsonlFiles(root: string, source: AiSessionSource): CandidateFile[] {
  if (!existsSync(root)) return [];

  const files: CandidateFile[] = [];
  const stack = [root];

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;

    let entries: string[];
    try {
      entries = readdirSync(current);
    } catch {
      continue;
    }

    for (const entry of entries) {
      const path = join(current, entry);
      let stats;
      try {
        stats = statSync(path);
      } catch {
        continue;
      }

      if (stats.isDirectory()) {
        stack.push(path);
        continue;
      }
      if (stats.isFile() && path.endsWith(".jsonl")) {
        files.push({ mtimeMs: stats.mtimeMs, path, source });
      }
    }
  }

  return files;
}

function readSessionImport(
  candidate: CandidateFile,
  root: string,
  options: {
    maxBytesPerFile: number;
  },
): ReadSessionImportResult | null {
  let stats;
  try {
    stats = statSync(candidate.path);
  } catch {
    return null;
  }
  if (stats.size > options.maxBytesPerFile) return null;

  const extractor = candidate.source === "codex" ? extractCodexMessageText : extractClaudeMessageText;
  const lines = readFileSync(candidate.path, "utf8").split(/\r?\n/);
  const relevantLines: string[] = [];
  let messageCount = 0;

  for (const line of lines) {
    const record = parseJsonLine(line);
    const text = extractor(record);
    if (!text) continue;
    messageCount += 1;
    relevantLines.push(...keepAuditRelevantLines(text));
  }

  const deduped = [...new Set(relevantLines)].slice(0, 80);
  if (deduped.length === 0) return null;

  const relativePath = relative(root, candidate.path);
  return {
    import: {
      sourceId: `${candidate.source}:${basename(candidate.path, ".jsonl")}`,
      sourceLabel: `${candidate.source === "codex" ? "Codex" : "Claude"} session ${relativePath}`,
      metadata: {
        bytes: stats.size,
        message_count: messageCount,
        relative_path: relativePath,
        retained_line_count: deduped.length,
        source_client: candidate.source,
      },
      text: deduped.join("\n"),
    },
    messageCount,
    retainedLineCount: deduped.length,
  };
}

export function loadAiSessionImports(options: LoadAiSessionImportsOptions): LoadAiSessionImportsResult {
  const now = options.now ?? new Date();
  const sinceMs = now.getTime() - (options.sinceDays ?? DEFAULT_SINCE_DAYS) * 24 * 60 * 60 * 1000;
  const limitPerSource = Math.max(1, options.limitPerSource ?? DEFAULT_LIMIT_PER_SOURCE);
  const maxBytesPerFile = Math.max(1, options.maxBytesPerFile ?? DEFAULT_MAX_BYTES_PER_FILE);
  const roots: Record<AiSessionSource, string> = {
    claude: options.claudeProjectsDir ?? CLAUDE_PROJECTS_DIR,
    codex: options.codexSessionsDir ?? CODEX_SESSIONS_DIR,
  };
  const imports: FounderLoopImport[] = [];
  const connectedSources: string[] = [];
  const missingSources: string[] = [];
  let retainedLines = 0;
  let scannedFiles = 0;
  let scannedMessages = 0;
  let skippedFiles = 0;

  for (const source of options.sources) {
    const root = roots[source];
    const candidates = collectJsonlFiles(root, source)
      .filter((file) => file.mtimeMs >= sinceMs)
      .sort((a, b) => b.mtimeMs - a.mtimeMs);

    if (candidates.length === 0) {
      missingSources.push(`${source} session store`);
      continue;
    }

    let importedForSource = 0;
    for (const candidate of candidates) {
      if (importedForSource >= limitPerSource) break;
      scannedFiles += 1;

      const result = readSessionImport(candidate, root, { maxBytesPerFile });
      if (!result) {
        skippedFiles += 1;
        continue;
      }

      imports.push(result.import);
      retainedLines += result.retainedLineCount;
      scannedMessages += result.messageCount;
      importedForSource += 1;
    }

    if (importedForSource > 0) {
      connectedSources.push(`${source === "codex" ? "Codex" : "Claude"} local sessions`);
    } else {
      missingSources.push(`${source} audit-relevant session lines`);
    }
  }

  return {
    connectedSources,
    imports,
    missingSources,
    retainedLines,
    scannedFiles,
    scannedMessages,
    skippedFiles,
  };
}
